import { RootState } from '@app/store/types';
import { getUser } from '@app/store/user/selectors';

/**
 * store/user/authority 전체 객체를 가져온다.
 */
const getUserAuthority = (state: RootState) => getUser(state).authority;

/**
 * 퍼미션 객체를 가져온다
 */
const getPermissions = (state: RootState) => getUserAuthority(state).permissions;

/**
 * 제한 객체를 가져온다
 */
const getLimits = (state: RootState) => getUserAuthority(state).limits;

/**
 * 요청 가능한 첨부 파일 개수를 반환한다.
 */
export const getAttachmentLimit = (state: RootState) =>
  getPermissions(state).DOCUMENT.id
    ? getLimits(state).ATTACHMENT_REQUEST_COUNT?.count ?? Infinity
    : 0;

/**
 * 대량전송 기능 권한 유무를 반환한다.
 */
export const getHasBulkRequestPermission = (state: RootState) =>
  !!getPermissions(state).BULK_REQUEST.id;

/**
 * 주소록 기능 권한 유무를 반환한다.
 */
export const getHasAddressBookPermission = (state: RootState) =>
  !!getPermissions(state).ADDRESS_BOOK.id;

/**
 * 문서 기능 권한 유무를 반환한다.
 */
export const getHasDocumentPermission = (state: RootState) => !!getPermissions(state).DOCUMENT.id;

/**
 * 보관 가능한 템플릿 개수를 반환한다.
 */
export const getTemplateLimit = (state: RootState) =>
  getPermissions(state).TEMPLATE.id ? getLimits(state).TEMPLATE_COUNT?.count ?? Infinity : 0;

export const getHasTemplatePermission = (state: RootState) => !!getPermissions(state).TEMPLATE.id;

/**
 * 문서 편집기 기능 권한 유무를 반환한다.
 */
export const getHasEditingDocumentPermission = (state: RootState): boolean =>
  !!getPermissions(state).EDITING_DOCUMENT.id;

/**
 * 이메일 맞춤 브랜딩 기능 권한 유무를 반환한다.
 */
export const getHasCustomBrandingEmailPermission = (state: RootState): boolean =>
  !!getPermissions(state).CUSTOM_BRANDING_EMAIL.id;

/**
 * 카카오톡 맞춤 브랜딩 기능 권한 유무를 반환한다.
 */
export const getHasCustomBrandingKakaoTalkChannelPermission = (state: RootState): boolean =>
  !!getPermissions(state).CUSTOM_BRANDING_KAKAO_CHANNEL.id;

/**
 * 외부문서 기능 권한 유무를 반환한다.
 */
export const getHasExternalDocumentPermission = (state: RootState): boolean =>
  !!getPermissions(state).EXTERNAL_DOCUMENT.id;

/**
 * 사용자의 권한 및 제한 조회 로딩 여부를 반환한다.
 */
export const getPermissionsAndLimitsLoadingStatus = (state: RootState) =>
  getUserAuthority(state).permissionsAndLimitsLoadingStatus;

/**
 * 사용자의 권한 및 제한, 사용량 조회 로딩 여부를 반환한다.
 */
export const getUserAuthorityLoadingStatus = (state: RootState) =>
  getUserAuthority(state).loadingStatus;

/**
 * 링크서명 권한 유무를 반환한다.
 */
export const getHasContractLinkPermission = (state: RootState) =>
  !!getPermissions(state).CONTRACT_LINK.id;

export const getHasInPersonSigningPermission = (state: RootState) =>
  !!getPermissions(state).IN_PERSON_CONTRACT.id;

/**
 * 예약전송 권한 유무를 반환한다.
 */
export const getHasScheduledRequestPermission = (state: RootState) =>
  !!getPermissions(state).SCHEDULED_REQUEST.id;
